import React from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import Tooltip from 'components/atomic/molecules/tooltip';
import { buildClassName } from 'utils/build-class-name';
import styles from './MortgageInputs.module.scss';

interface MortgageInputsProps {
  homePrice: number;
  downPayment: number;
  interestRate: number;
  amortization: number;
  mortgageType: string;
  paymentFrequency: string;
  propertyTax: number;
  condoFees: number;
  heatingCosts: number;
  onHomePriceChange: (value: number) => void;
  onDownPaymentChange: (value: number) => void;
  onInterestRateChange: (value: number) => void;
  onAmortizationChange: (value: number) => void;
  onMortgageTypeChange: (value: string) => void;
  onPaymentFrequencyChange: (value: string) => void;
  onPropertyTaxChange: (value: number) => void;
  onCondoFeesChange: (value: number) => void;
  onHeatingCostsChange: (value: number) => void;
}

const MortgageInputs = ({
  homePrice,
  downPayment,
  interestRate,
  amortization,
  mortgageType,
  paymentFrequency,
  propertyTax,
  condoFees,
  heatingCosts,
  onHomePriceChange,
  onDownPaymentChange,
  onInterestRateChange,
  onAmortizationChange,
  onMortgageTypeChange,
  onPaymentFrequencyChange,
  onPropertyTaxChange,
  onCondoFeesChange,
  onHeatingCostsChange,
}: MortgageInputsProps) => {
  const downPaymentPercent = ((downPayment / homePrice) * 100).toFixed(1);

  return (
    <div className={styles.component}>
      {/* Home Price */}
      <div className={styles.inputGroup}>
        <div className={styles.inputHeader}>
          <Label htmlFor="homePrice" className={styles.inputLabel}>
            Home Price
            <Tooltip content="The total purchase price of the property" placement="top" tooltipSx={{ maxWidth: '18rem' }}>
              <span className={styles.infoIcon}>ℹ️</span>
            </Tooltip>
          </Label>
          <span className={styles.inputValue}>${homePrice.toLocaleString()}</span>
        </div>
        <Slider
          value={[homePrice]}
          onValueChange={([value]) => onHomePriceChange(value)}
          min={100000}
          max={3000000}
          step={10000}
        />
        <Input
          id="homePrice"
          type="number"
          value={homePrice}
          onChange={e => onHomePriceChange(Number(e.target.value))}
          style={{ marginTop: '0.5rem' }}
        />
      </div>

      {/* Down Payment */}
      <div className={styles.inputGroup}>
        <div className={styles.inputHeader}>
          <Label htmlFor="downPayment" className={styles.inputLabel}>
            Down Payment
            <Tooltip content="Minimum 5% for homes under $500k, 10% for the portion over $500k, 20% for homes over $1M" placement="top" tooltipSx={{ maxWidth: '18rem' }}>
              <span className={styles.infoIcon}>ℹ️</span>
            </Tooltip>
          </Label>
          <span className={styles.inputValue}>${downPayment.toLocaleString()} ({downPaymentPercent}%)</span>
        </div>
        <div className={styles.quickSelectButtons}>
          {[5, 10, 15, 20].map(percent => {
            const exactValue = homePrice * (percent / 100);
            const isExact = downPayment === exactValue;
            const actualPercent = (downPayment / homePrice) * 100;
            const isNearby = !isExact && Math.abs(actualPercent - percent) < 2;
            return (
              <button
                key={percent}
                onClick={() => onDownPaymentChange(exactValue)}
                className={buildClassName(
                  styles.quickSelectButton,
                  isExact && styles.active,
                  isNearby && styles.nearby
                )}
              >
                {percent}%
              </button>
            );
          })}
        </div>
        <Slider
          value={[downPayment]}
          onValueChange={([value]) => onDownPaymentChange(value)}
          min={homePrice * 0.05}
          max={homePrice * 0.9}
          step={1000}
        />
        <Input
          id="downPayment"
          type="number"
          value={downPayment}
          onChange={e => onDownPaymentChange(Number(e.target.value))}
          style={{ marginTop: '0.5rem' }}
        />
      </div>

      {/* Interest Rate */}
      <div className={styles.inputGroup}>
        <Label htmlFor="interestRate" className={styles.inputLabel}>
          Interest Rate
          <Tooltip content="The annual interest rate for your mortgage" placement="top" tooltipSx={{ maxWidth: '18rem' }}>
            <span className={styles.infoIcon}>ℹ️</span>
          </Tooltip>
        </Label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Select
            value={interestRate.toFixed(2)}
            onValueChange={value => onInterestRateChange(Number(value))}
          >
            <SelectTrigger style={{ flex: 1 }}>
              <SelectValue placeholder="Select rate" />
            </SelectTrigger>
            <SelectContent style={{ maxHeight: '15rem', backgroundColor: '$color-white' }}>
              {Array.from({ length: 39 }, (_, i) => (0.5 + i * 0.25).toFixed(2)).map(rate => (
                <SelectItem key={rate} value={rate}>
                  {rate}%
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            id="interestRate"
            type="number"
            value={interestRate}
            onChange={e => onInterestRateChange(Number(e.target.value))}
            step="0.01"
            min="0.5"
            max="15"
            style={{ width: '6rem' }}
            placeholder="Custom"
          />
        </div>
      </div>

      {/* Amortization Period */}
      <div className={styles.inputGroup}>
        <div className={styles.inputHeader}>
          <Label htmlFor="amortization" className={styles.inputLabel}>
            Amortization Period
            <Tooltip content="The total length of time to pay off your mortgage" placement="top" tooltipSx={{ maxWidth: '18rem' }}>
              <span className={styles.infoIcon}>ℹ️</span>
            </Tooltip>
          </Label>
          <span className={styles.inputValue}>{amortization} years</span>
        </div>
        <Slider
          value={[amortization]}
          onValueChange={([value]) => onAmortizationChange(value)}
          min={5}
          max={30}
          step={1}
        />
      </div>

      {/* Mortgage Type */}
      <div className={styles.inputGroup}>
        <Label htmlFor="mortgageType">Mortgage Type</Label>
        <Select value={mortgageType} onValueChange={onMortgageTypeChange}>
          <SelectTrigger id="mortgageType">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fixed">Fixed Rate</SelectItem>
            <SelectItem value="variable">Variable Rate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Payment Frequency */}
      <div className={styles.inputGroup}>
        <Label htmlFor="paymentFrequency">Payment Frequency</Label>
        <Select value={paymentFrequency} onValueChange={onPaymentFrequencyChange}>
          <SelectTrigger id="paymentFrequency">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="biweekly">Bi-weekly</SelectItem>
            <SelectItem value="accelerated-biweekly">Accelerated Bi-weekly</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Additional Costs */}
      <div className={styles.additionalCosts}>
        <h3 className={styles.additionalCostsTitle}>Additional Costs</h3>

        <div className={styles.inputGroup}>
          <Label htmlFor="propertyTax">Annual Property Tax</Label>
          <Input
            id="propertyTax"
            type="number"
            value={propertyTax || ''}
            onChange={e => onPropertyTaxChange(Number(e.target.value) || 0)}
            placeholder="0"
          />
        </div>

        <div className={styles.inputGroup}>
          <Label htmlFor="condoFees">Monthly Condo Fees</Label>
          <Input
            id="condoFees"
            type="number"
            value={condoFees || ''}
            onChange={e => onCondoFeesChange(Number(e.target.value) || 0)}
            placeholder="0"
          />
        </div>

        <div className={styles.inputGroup}>
          <Label htmlFor="heatingCosts">Annual Heating Costs</Label>
          <Input
            id="heatingCosts"
            type="number"
            value={heatingCosts || ''}
            onChange={e => onHeatingCostsChange(Number(e.target.value) || 0)}
            placeholder="0"
          />
        </div>
      </div>
    </div>
  );
};

export default MortgageInputs;
